'use client'

import { getAnalyticsOptOutAction } from '@/app/actions/analytics'
import { createBrowserClient } from '@vagahub/db/browser'
import { usePathname, useSearchParams } from 'next/navigation'
import { usePostHog } from 'posthog-js/react'
import { Suspense, useEffect } from 'react'

function AnalyticsWatcherInner() {
  const posthog = usePostHog()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Mitigação #4: Capturar manualmente visualizações de página a cada mudança de URL
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname
      const query = searchParams?.toString()
      if (query) {
        url = `${url}?${query}`
      }
      posthog.capture('$pageview', {
        $current_url: url,
      })
    }
  }, [pathname, searchParams, posthog])

  useEffect(() => {
    const supabase = createBrowserClient()

    const checkUserPreferences = async (_userId: string) => {
      try {
        const optedOut = await getAnalyticsOptOutAction()

        if (optedOut) {
          posthog?.opt_out_capturing()
        } else {
          // Local fallback if user allows but no explicit cookie opt-out
          const localOptOut = localStorage.getItem('vagahub_lgpd_opt_out') === 'true'
          if (localOptOut) {
            posthog?.opt_out_capturing()
          } else {
            posthog?.opt_in_capturing()
          }
        }
      } catch (_err) {
        // Fallback silently
      }
    }

    // Check current user immediately on mount
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        // Mitigação #1: Identificar apenas por ID anônimo, eliminando PII (email/nome)
        posthog?.identify(data.user.id)
        checkUserPreferences(data.user.id)
      } else {
        // Anonymous user check local preferences
        const localOptOut = localStorage.getItem('vagahub_lgpd_opt_out') === 'true'
        if (localOptOut) {
          posthog?.opt_out_capturing()
        }
      }
    })

    // Listen to sign in / sign out events
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Mitigação #1: Identificar apenas por ID anônimo
        posthog?.identify(session.user.id)
        checkUserPreferences(session.user.id)
      } else if (event === 'SIGNED_OUT') {
        posthog?.reset() // Clear user identity data
      }
    })

    return () => {
      listener.subscription.unsubscribe()
    }
  }, [posthog])

  return null // This is a background utility component
}

export function AnalyticsWatcher() {
  return (
    <Suspense fallback={null}>
      <AnalyticsWatcherInner />
    </Suspense>
  )
}
